/**
 * Invisible rate limiting utilities for abuse prevention
 * Designed to be unnoticeable during normal usage
 */

import { useCallback, useRef, useState, useEffect } from 'react'

// Rate limiter for API endpoints (in-memory tracking)
const apiCallTracker = new Map<string, { calls: number[]; blocked: boolean }>()

/**
 * Creates an invisible rate limiter that only blocks obvious abuse
 * Normal users will never hit these limits
 */
export function createAbusePrevention(
  endpoint: string,
  maxCalls = 30, // Very generous - 30 calls per minute
  windowMs = 60000 // 1 minute window
) {
  return {
    canProceed: (): boolean => {
      const now = Date.now()
      const tracker = apiCallTracker.get(endpoint) || { calls: [], blocked: false }

      // Clean old calls outside the window
      tracker.calls = tracker.calls.filter((callTime) => now - callTime < windowMs)

      // Check if we're already blocked (prevents spam during block period)
      if (tracker.blocked && tracker.calls.length > 0) {
        const oldestCall = Math.min(...tracker.calls)
        if (now - oldestCall < windowMs) {
          return false // Still in block period
        }
        tracker.blocked = false // Block period expired
      }

      // Allow if under limit
      if (tracker.calls.length < maxCalls) {
        tracker.calls.push(now)
        apiCallTracker.set(endpoint, tracker)
        return true
      }

      // Block abuse - this should only happen with spam
      tracker.blocked = true
      apiCallTracker.set(endpoint, tracker)
      return false
    },

    reset: () => {
      apiCallTracker.delete(endpoint)
    }
  }
}

/**
 * Invisible debounce hook - only delays rapid-fire typing
 * Normal typing (even fast) won't be affected
 */
export function useAntiSpamDebounce<T>(
  value: T,
  delay = 150 // Very short - only blocks machine-like speed
): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const lastChangeRef = useRef<number>(0)

  useEffect(() => {
    const now = Date.now()
    const timeSinceLastChange = now - lastChangeRef.current

    // If changes are coming too fast (< 50ms apart), it's likely spam
    // Normal fast typing is ~100-200ms between keystrokes
    if (timeSinceLastChange < 50) {
      // Clear existing timeout and set new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
    } else {
      // Normal speed - update immediately
      setDebouncedValue(value)
    }

    lastChangeRef.current = now

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * Invisible cooldown for action buttons
 * Only prevents machine-like rapid clicking (< 100ms between clicks)
 */
export function useAntiSpamCooldown(
  cooldownMs = 500 // Half second - prevents accidental double-clicks
) {
  const [isInCooldown, setIsInCooldown] = useState(false)
  const lastActionRef = useRef<number>(0)

  const executeWithCooldown = useCallback(
    (action: () => void | Promise<void>) => {
      const now = Date.now()
      const timeSinceLastAction = now - lastActionRef.current

      // Only block if clicking way too fast (machine-like behavior)
      if (timeSinceLastAction < 100) {
        return // Ignore obvious spam clicks
      }

      // Normal cooldown for accidental double-clicks
      if (isInCooldown) {
        return
      }

      setIsInCooldown(true)
      lastActionRef.current = now

      Promise.resolve(action()).finally(() => {
        setTimeout(() => {
          setIsInCooldown(false)
        }, cooldownMs)
      })
    },
    [cooldownMs, isInCooldown]
  )

  return {
    executeWithCooldown,
    isInCooldown
  }
}

/**
 * Background task rate limiter - prevents excessive polling
 * Automatically backs off if the same endpoint is called too frequently
 */
export function createBackgroundRateLimiter(
  endpoint: string,
  minIntervalMs = 30000 // 30 seconds minimum between background calls
) {
  const lastCallKey = `bg_${endpoint}`

  return {
    shouldExecute: (): boolean => {
      const tracker = apiCallTracker.get(lastCallKey)
      const now = Date.now()

      if (!tracker || tracker.calls.length === 0) {
        apiCallTracker.set(lastCallKey, { calls: [now], blocked: false })
        return true
      }

      const lastCall = tracker.calls[tracker.calls.length - 1]
      const timeSinceLastCall = now - lastCall

      if (timeSinceLastCall >= minIntervalMs) {
        tracker.calls = [now] // Keep only the latest call for background tasks
        apiCallTracker.set(lastCallKey, tracker)
        return true
      }

      return false
    }
  }
}

/**
 * Smart polling that adapts to user activity
 * Polls faster when user is active, slower when idle
 */
export function useAdaptivePolling(
  baseIntervalMs = 30000 // 30 seconds base
) {
  const [isUserActive, setIsUserActive] = useState(true)
  const lastActivityRef = useRef<number>(Date.now())

  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now()
      setIsUserActive(true)
    }

    const checkActivity = () => {
      const now = Date.now()
      const timeSinceActivity = now - lastActivityRef.current

      // Consider user idle after 2 minutes of no interaction
      if (timeSinceActivity > 120000) {
        setIsUserActive(false)
      }
    }

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true)
    })

    // Check activity every 30 seconds
    const activityChecker = setInterval(checkActivity, 30000)

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true)
      })
      clearInterval(activityChecker)
    }
  }, [])

  // Return adaptive interval: normal when active, 3x slower when idle
  return isUserActive ? baseIntervalMs : baseIntervalMs * 3
}
